/**
 * engine/submission.ts — final-results phase-boundary submission.
 *
 * Owns resolution-time bookkeeping: when the engine resolves with
 * final results, this fires any phase boundaries that the progress-tick
 * path didn't reach (e.g. a phase that ended on its last sample without
 * crossing into the next phase's progress event), then fires the
 * "complete" snapshot. At resolution every metric's final value is
 * known, so any boundary latch still false gets filled in here.
 */

import type { SpeedtestResults as EngineResults } from "@speedtest/index.ts";

import { TestState } from "../types.ts";
import type { MetricStates, PhaseCompletePayload } from "../types.ts";
import type { ProgressTracker } from "./progress.ts";

export interface SubmissionContext {
    progress: ProgressTracker;
    submitPhaseSnapshot: (
        snapshot: PhaseCompletePayload,
        status: "partial" | "complete",
    ) => void;
}

/**
 * Apply final results to the reactive state: snapshot data to phase
 * "finished" + mark every metric finished. Called from the subscription
 * module's resolution branch alongside `submitFinal`.
 */
export function applyFinal(
    progress: ProgressTracker,
    metricStates: MetricStates,
    r: EngineResults,
): void {
    progress.data.value = {
        phase: "finished",
        downloadSpeed: r.download.speedMbps,
        uploadSpeed: r.upload.speedMbps,
        ping: r.ping.latency,
        jitter: r.ping.jitter,
        downloadProgress: 1,
        uploadProgress: 1,
        pingProgress: 1,
    };
    metricStates.ping = TestState.finished;
    metricStates.download = TestState.finished;
    metricStates.upload = TestState.finished;
}

/** Console-log the final results — kept here so the subscription module
 *  stays focused on lifecycle wiring. */
export function logFinal(r: EngineResults): void {
    console.log(
        `[speedtest] ping=${r.ping.latency.toFixed(0)}ms jitter=${r.ping.jitter.toFixed(0)}ms`,
    );
    console.log(
        `[speedtest] download=${r.download.speedMbps.toFixed(1)}Mbps raw=${r.download.rawSpeedMbps.toFixed(1)} stability=${r.download.stability.toFixed(2)}`,
    );
    console.log(
        `[speedtest] upload=${r.upload.speedMbps.toFixed(1)}Mbps raw=${r.upload.rawSpeedMbps.toFixed(1)} stability=${r.upload.stability.toFixed(2)}`,
    );
}

/**
 * Fire any phase boundaries not yet fired, then the final "complete"
 * snapshot, given the engine's resolved results.
 *
 * Called exclusively from the subscription module's `run().then(...)`
 * branch. Ordering matters: ping → download → upload — each "partial"
 * carries the cumulative state up to that point so the server's
 * upsert-by-`sessionId` evolves the same row.
 */
export function submitFinal(ctx: SubmissionContext, r: EngineResults): void {
    const { progress, submitPhaseSnapshot } = ctx;

    if (!progress.phaseBoundaryFired.ping) {
        progress.markPhaseBoundaryFired("ping");
        submitPhaseSnapshot(
            {
                ping: r.ping.latency,
                jitter: r.ping.jitter,
                download: null,
                upload: null,
            },
            "partial",
        );
    }
    if (!progress.phaseBoundaryFired.download) {
        progress.markPhaseBoundaryFired("download");
        submitPhaseSnapshot(
            {
                ping: r.ping.latency,
                jitter: r.ping.jitter,
                download: r.download.speedMbps,
                upload: null,
            },
            "partial",
        );
    }
    progress.markPhaseBoundaryFired("upload");
    submitPhaseSnapshot(
        {
            ping: r.ping.latency,
            jitter: r.ping.jitter,
            download: r.download.speedMbps,
            upload: r.upload.speedMbps,
        },
        "complete",
    );
}
